/*
ForegroundObjectDetail.js
wgbh-skydiagram-testing
astro.unl.edu
2019-08-02
*/


import * as React from 'react';

import {Slider, Button, Checkbox, FormGroup, InputGroup, HTMLSelect} from '@blueprintjs/core';

import {ForegroundObjectVisibilityDetail} from './ForegroundObjectVisibilityDetail';


/*

Props (required):
	workspace
	onSelect
	selected		- may be null

*/


export class ForegroundObjectDetail extends React.Component {

	constructor(props) {
		super(props);

		this.forceRender = this.forceRender.bind(this);
		this.objectManagerChangeCallback = this.objectManagerChangeCallback.bind(this);
		this.handleSetObjectParams = this.handleSetObjectParams.bind(this);
		this.handleSetName = this.handleSetName.bind(this);
		this.handleSetReferenceById = this.handleSetReferenceById.bind(this);
		this.handleDeleteObject = this.handleDeleteObject.bind(this);

		this._paramsMan = this.props.workspace.getParamsManager();

		this._registeredObject = null;

		this.state = {
			dirtyCount: 1,
		};
	}


	/*
	**	Callbacks and Handlers
	*/

	forceRender() {
		this.setState(state => ({dirtyCount: state.dirtyCount+1}));
	}

	objectManagerChangeCallback(type, caller, data) {
		if (caller === this.props.selected) {
			this.forceRender();
		} else {
			console.error('The foreground object detail panel received a change callback from an unselected object.');
		}
	}

	handleSetObjectParams(p) {
		if (this.props.selected !== null) {
			this.props.selected.setParams(p);
		} else {
			console.error('An attempt was made to set the name of a null foreground object.');
		}
	}

	handleSetName(name) {
		if (this.props.selected !== null) {
			this.props.selected.setName(name);
		} else {
			console.error('An attempt was made to set the name of a null foreground object.');
		}
	}

	handleSetReferenceById(id) {
		if (this.props.selected !== null) {
			let reference = this._paramsMan.getForegroundObjectWithId(id);
			this.props.selected.setReference(reference);
		} else {
			console.error('An attempt was made to set the reference property of a null foreground object.');
		}
	}

	handleDeleteObject() {
		if (this.props.selected !== null) {
			this._paramsMan.deleteForegroundObject(this.props.selected);
		} else {
			console.error('An attempt was made to delete a null foreground object.');
		}
	}


	/*
	**	Other Methods
	*/

	checkIfObjectChanged() {
		// This panel needs to get change callbacks on the currently selected object. This
		//	requires registering and unregistering for callbacks as the selected object changes.

		if (this.props.selected === this._registeredObject) {
			// No change -- nothing to do.
			return;
		}

		if (this._registeredObject !== null) {
			this._registeredObject.unregisterCallback('change', this.objectManagerChangeCallback);
		}

		this._registeredObject = this.props.selected;

		if (this._registeredObject !== null) {
			this._registeredObject.registerCallback('change', this.objectManagerChangeCallback);
		}
	}


	render() {

		this.checkIfObjectChanged();
		
		let content;

		if (this.props.selected !== null) {

			let fobj = this.props.selected;
			let params = fobj.getParams();

			// Assemble the thumbnail.
			let thumb;
			let image = fobj.getImage();
			let imgSrc = (image !== null) ? image.getSrc() : "";
			if (image === null) {
				thumb = (<p>no image</p>);
			} else {
				thumb = (<img src={imgSrc} alt="" style={{maxWidth: "500px", maxHeight: "80px"}} />);
			}

			// Construct the system options.
			let systemOptions = [
				{	label: 'horizon',
					value: 'horizon',
					disabled: false,
				},
				{	label: 'object',
					value: 'object',
					disabled: fobj.getIsUsedAsReference(),
				},
			];

			// Construct the reference options.
			let referenceOptions = [{label: '<none>', value: '', disabled: false}];
			let fobjs = this._paramsMan.getForegroundObjects();
			for (let i = 0; i < fobjs.length; ++i) {
				let isEnabled = ( (fobjs[i] !== this.props.selected) && fobjs[i].getCanBeUsedAsReference() );
				referenceOptions.push({label: fobjs[i].getName(), value: fobjs[i].getId(), disabled: !isEnabled});
			}

			// Determined the select element value for the current reference.
			let currRefId = '';
			let currRef = this.props.selected.getReference();
			if (currRef !== null) {
				currRefId = currRef.getId();
			}

			content = (
				<div>
					
					{thumb}

					<FormGroup label="Name" labelFor="name-input" style={{margin: "20px 0"}}>
						<InputGroup id="name-input" onChange={(e) => this.handleSetName(e.target.value)} value={fobj.getName()} />
					</FormGroup>

					<FormGroup label="Geometry System" labelFor="system-select" style={{margin: "20px 0"}}>
						<HTMLSelect id="system-select" options={systemOptions} value={params.system}
							onChange={(e) => this.handleSetObjectParams({system: e.target.value})} />
					</FormGroup>

					<FormGroup label="Reference Object" labelFor="reference-select" style={{margin: "20px 0"}}
						helperText="Ignored when in horizon system." >
						<HTMLSelect id="reference-select" onChange={(e) => this.handleSetReferenceById(e.target.value)}
							value={currRefId} options={referenceOptions} />
					</FormGroup>

					<FormGroup label="Width" labelFor="object-width-slider">
					<Slider id="object-width-slider" min={0.01} max={1.5} stepSize={0.001} labelStepSize={0.2}
						onChange={value => this.handleSetObjectParams({width: value})} value={params.width} />
					</FormGroup>

					<FormGroup label="X-Position" labelFor="x-slider">
					<Slider id="x-slider" min={-0.5} max={1.5} stepSize={0.01} labelStepSize={0.25}
						onChange={value => this.handleSetObjectParams({x: value})} value={params.x} />
					</FormGroup>

					<FormGroup label="Y-Position" labelFor="y-slider">
					<Slider id="y-slider" min={-0.5} max={1.5} stepSize={0.01} labelStepSize={0.25}
						onChange={value => this.handleSetObjectParams({y: value})} value={params.y} />
					</FormGroup>

					<FormGroup label="Offset-X" labelFor="offset-x-slider">
					<Slider id="offset-x-slider" min={0.0} max={1.0} stepSize={0.01} labelStepSize={0.25}
						onChange={value => this.handleSetObjectParams({offsetX: value})} value={params.offsetX} />
					</FormGroup>

					<FormGroup label="Offset-Y" labelFor="offset-y-slider">
					<Slider id="offset-y-slider" min={0.0} max={1.0} stepSize={0.01} labelStepSize={0.25}
						onChange={value => this.handleSetObjectParams({offsetY: value})} value={params.offsetY} />
					</FormGroup>

					<Checkbox id="apply-night-shading-check" label="Shade at Night"
						checked={params.applyNightShading}
						onChange={e => this.handleSetObjectParams({applyNightShading: e.target.checked})}
						style={{margin: "20px 0 20px 0"}} />

					<ForegroundObjectVisibilityDetail selected={this.props.selected}/>

					<Button icon="delete" disabled={!fobj.getCanDelete()} onClick={this.handleDeleteObject}
						style={{marginTop: "50px", display: "block", position: "relative", left: "50%", transform: "translateX(-50%)"}} >
						Delete This Object
					</Button>

				</div>
			);

		} else {

			content = (<p>Select an object from the list at left.</p>);
		}

		return (
			<div style={{width: "550px", margin: "0 0 0 40px", display: "inline-block", verticalAlign: 'top'}}>
				{content}
			</div>
		);
	}

}


